/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { useGroups } from "../../hooks/custom/useGroups";
import { useRecipientsSelection } from "../../hooks/custom/useRecipientsSelection";
import CreateGroupModal from "../modals/CreateGroupModal";
import EditGroupModal from "../modals/EditGroupModal";
import DeleteGroupModal from "../modals/DeleteGroupModal";
import RecipientsSelectionModal from "../modals/RecipientsSelectionModal";
import GroupsTable from "../tables/GroupsTable";
import GroupsHeader from "../ui/GroupsHeader";
import { useToast } from "../../contexts/ToastContext";

const RecipientGroups: React.FC = () => {
  const { showToast } = useToast();

  const {
    groups,
    groupsLoading,
    groupsError,
    searchTerm,
    setSearchTerm,
    editingGroup,
    setEditingGroup,
    deletingGroup,
    setDeletingGroup,
    handleCreateGroup,
    handleEditGroup,
    handleUpdateGroup,
    handleDeleteGroup,
    confirmDeleteGroup,
    handleRemoveRecipientFromGroup,
  } = useGroups();

  const {
    handleSelectRecipients,
    onRecipientSelection,
    handleRecipientsPageChange,
    handleRecipientsItemsPerPageChange,
    handleSelectAllFromDB: handleSelectAllRecipientsFromDB,
    handleSelectAllVisible,
    handleClearAllSelections,
    handleLoadAllRecipients,
    handleAddSelectedRecipients,
  } = useRecipientsSelection(
    () => {}, // setRecipientsLoading
    () => {}, // setAvailableRecipients
    () => {}, // setRecipientsTotalCount
    () => {}, // setAllRecipients
    () => {}, // setSelectedAllFromDB
    () => {}, // setHasLoadedAllRecipients
    () => {}, // setLoadingMoreRecipients
    [], // allRecipients
    1, // recipientsCurrentPage
    50, // recipientsItemsPerPage
    [], // selectedRecipientIds
    () => {}, // setSelectedRecipientIds
    [], // availableRecipients
    () => {}, // setShowRecipientsModal
    () => {}, // setRecipientsCurrentPage
    () => {}, // setSelectedRecipients
    () => {}, // setShowCreateGroupModal
    () => {}, // setShowEditGroupModal
    () => {}, // setRecipientsItemsPerPage
    [] // selectedRecipients
  );

  // Local state for modals
  const [showCreateGroupModal, setShowCreateGroupModal] = React.useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = React.useState(false);

  const [newGroup, setNewGroup] = React.useState({
    name: "",
    description: "",
    recipients: [] as any[],
  });

  const [selectedRecipients, setSelectedRecipients] = React.useState<any[]>([]);
  const [availableRecipients, setAvailableRecipients] = React.useState<any[]>(
    []
  );
  const [recipientsLoading, setRecipientsLoading] = React.useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = React.useState<
    string[]
  >([]);
  const [recipientsSearchTerm, setRecipientsSearchTerm] = React.useState("");
  const [recipientsCurrentPage, setRecipientsCurrentPage] = React.useState(1);
  const [recipientsItemsPerPage, setRecipientsItemsPerPage] =
    React.useState(50);
  const [recipientsTotalCount, setRecipientsTotalCount] = React.useState(0);
  const [allRecipients, setAllRecipients] = React.useState<any[]>([]);
  const [selectedAllFromDB, setSelectedAllFromDB] = React.useState(false);
  const [hasLoadedAllRecipients, setHasLoadedAllRecipients] =
    React.useState(false);
  const [loadingMoreRecipients, setLoadingMoreRecipients] =
    React.useState(false);

  const handleCloseCreateModal = () => {
    setShowCreateGroupModal(false);
    setNewGroup({ name: "", description: "", recipients: [] });
    setSelectedRecipients([]);
  };

  const handleCloseEditModal = () => {
    setShowEditGroupModal(false);
    setEditingGroup(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingGroup(null);
  };

  const handleCloseRecipientsModal = () => {
    setShowRecipientsModal(false);
    setSelectedRecipientIds([]);
    setSelectedAllFromDB(false);
  };

  if (groupsError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Error Loading Groups</h3>
        <p className="text-red-600 mt-1">{groupsError}</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <GroupsHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onAddGroup={() => setShowCreateGroupModal(true)}
      />

      <GroupsTable
        groups={groups}
        groupsLoading={groupsLoading}
        groupsError={groupsError}
        onEditGroup={handleEditGroup}
        onDeleteGroup={handleDeleteGroup}
      />

      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={handleCloseCreateModal}
        newGroup={newGroup}
        setNewGroup={setNewGroup}
        selectedRecipients={selectedRecipients}
        onRemoveRecipient={handleRemoveRecipientFromGroup}
        onSelectRecipients={() => {
          handleSelectRecipients();
          setShowRecipientsModal(true);
        }}
        onAddGroup={handleCreateGroup}
      />

      {editingGroup && (
        <EditGroupModal
          isOpen={showEditGroupModal}
          onClose={handleCloseEditModal}
          editingGroup={editingGroup}
          newGroup={newGroup}
          setNewGroup={setNewGroup}
          selectedRecipients={selectedRecipients}
          onRemoveRecipient={handleRemoveRecipientFromGroup}
          onSelectRecipients={() => {
            handleSelectRecipients();
            setShowRecipientsModal(true);
          }}
          onUpdateGroup={handleUpdateGroup}
        />
      )}

      {deletingGroup && (
        <DeleteGroupModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          deletingGroup={deletingGroup}
          onConfirmDelete={confirmDeleteGroup}
        />
      )}

      <RecipientsSelectionModal
        isOpen={showRecipientsModal}
        onClose={handleCloseRecipientsModal}
        availableRecipients={availableRecipients}
        recipientsLoading={recipientsLoading}
        recipientsSearchTerm={recipientsSearchTerm}
        setRecipientsSearchTerm={setRecipientsSearchTerm}
        selectedRecipientIds={selectedRecipientIds}
        onRecipientSelection={onRecipientSelection}
        recipientsCurrentPage={recipientsCurrentPage}
        recipientsItemsPerPage={recipientsItemsPerPage}
        recipientsTotalCount={recipientsTotalCount}
        handleRecipientsPageChange={handleRecipientsPageChange}
        handleRecipientsItemsPerPageChange={handleRecipientsItemsPerPageChange}
        handleSelectAllFromDB={handleSelectAllRecipientsFromDB}
        handleSelectAllVisible={handleSelectAllVisible}
        handleClearAllSelections={handleClearAllSelections}
        handleLoadAllRecipients={handleLoadAllRecipients}
        handleAddSelectedRecipients={handleAddSelectedRecipients}
        selectedAllFromDB={selectedAllFromDB}
        hasLoadedAllRecipients={hasLoadedAllRecipients}
        loadingMoreRecipients={loadingMoreRecipients}
        filteredAvailableRecipients={availableRecipients}
        recipientsTotalPages={Math.ceil(
          recipientsTotalCount / recipientsItemsPerPage
        )}
        allRecipients={allRecipients}
      />
    </div>
  );
};

export default RecipientGroups;
